import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tab from '@mui/material/Tab';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { canManageAdminPlans, canViewAdminPlans } from '../../helpers/authSession';
import FormSection from '../../components/shared/FormSection';
import SuccessToast from '../../components/shared/SuccessToast';
import AuditStatusConfirmDialog from '../../components/shared/AuditStatusConfirmDialog';
import { asDisplayText, formatTemporalHumanized } from '../../helpers/dateTimeFormat';
import RulesSelectionPreview from '../../components/RulesPlan/RulesSelectionPreview';
import {
  createAdminPlan,
  getAdminPlanRules,
  getAdminPlans,
  getAdminRuleCatalogs,
  updateAdminPlan,
} from '../../services/complianceRules';
import { getRulesEngineUiMessage } from '../../services/rulesEngineErrors';

const COAF_GROUP_ORDER = [
  'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
  'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'CIRCULAR_3978',
];

const TAB_VALUES = {
  create: 'create',
  edit: 'edit',
};

const PLAN_STATUS_OPTIONS = ['draft', 'active', 'retired'];
const PLAN_STATUS_LABELS = {
  draft: 'Rascunho',
  active: 'Ativo',
  retired: 'Arquivado',
};

const resolvePlanStatusLabel = (value) => PLAN_STATUS_LABELS[value] || asDisplayText(value);
const resolveLatestChangeSummary = (entry) => {
  if (!entry) {
    return '-';
  }

  const changedBy = asDisplayText(entry.changed_by || entry.changedBy);
  const reason = asDisplayText(entry.reason);

  if (changedBy && reason) {
    return `${changedBy} - ${reason}`;
  }

  return changedBy || reason || '-';
};

const resolveInitialState = () => ({
  name: '',
  code: '',
  description: '',
  targetSegment: '',
  version: '1',
  status: 'draft',
  effectiveFrom: '',
  effectiveTo: '',
});

const buildPlanFormFromPlan = (plan) => ({
  name: asDisplayText(plan?.raw?.name) === '-' ? '' : asDisplayText(plan?.raw?.name),
  code: asDisplayText(plan?.raw?.code) === '-' ? '' : asDisplayText(plan?.raw?.code),
  description: asDisplayText(plan?.raw?.description) === '-' ? '' : asDisplayText(plan?.raw?.description),
  targetSegment: asDisplayText(plan?.raw?.target_segment) === '-' ? '' : asDisplayText(plan?.raw?.target_segment),
  version: asDisplayText(plan?.raw?.version) === '-' ? '' : asDisplayText(plan?.raw?.version),
  status: asDisplayText(plan?.raw?.status) === '-' ? 'draft' : asDisplayText(plan?.raw?.status),
  effectiveFrom: toDateInputValue(plan?.raw?.effective_from),
  effectiveTo: toDateInputValue(plan?.raw?.effective_to),
});

const toDateInputValue = (value) => {
  const text = String(value || '').trim();
  if (!text) {
    return '';
  }

  return text.length >= 10 ? text.slice(0, 10) : text;
};

const parseDateStart = (value) => {
  const text = String(value || '').trim();
  if (!text) {
    return null;
  }

  const parsed = new Date(`${text}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export default function AdminPlans() {
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [plans, setPlans] = React.useState([]);
  const [ruleCatalogs, setRuleCatalogs] = React.useState([]);
  const [createFormState, setCreateFormState] = React.useState(resolveInitialState);
  const [createFromArchivedSource, setCreateFromArchivedSource] = React.useState(null);
  const [selectedGroups, setSelectedGroups] = React.useState([]);
  const [editingPlanId, setEditingPlanId] = React.useState('');
  const [editingPlanCurrentStatus, setEditingPlanCurrentStatus] = React.useState('');
  const [editingPlanExistingGroups, setEditingPlanExistingGroups] = React.useState([]);
  const [isLoadingEditGroups, setIsLoadingEditGroups] = React.useState(false);
  const [isEditingEffectiveFromLocked, setIsEditingEffectiveFromLocked] = React.useState(false);
  const [isEditingEffectiveToLocked, setIsEditingEffectiveToLocked] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(TAB_VALUES.create);
  const [planStatusFilter, setPlanStatusFilter] = React.useState('all');
  const [formState, setFormState] = React.useState(resolveInitialState);
  const [editSelectedGroups, setEditSelectedGroups] = React.useState([]);
  const [editReason, setEditReason] = React.useState('');
  const [archivePlanDialog, setArchivePlanDialog] = React.useState({ open: false, reason: '' });
  const [errorMessage, setErrorMessage] = React.useState('');
  const [successMessage, setSuccessMessage] = React.useState('');

  const canView = canViewAdminPlans();
  const canManage = canManageAdminPlans();

  const loadPlans = React.useCallback(async () => {
    if (!canView) {
      setPlans([]);
      setRuleCatalogs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const [plansResponse, catalogsResponse] = await Promise.all([
        getAdminPlans(),
        getAdminRuleCatalogs(),
      ]);

      setPlans(plansResponse);
      setRuleCatalogs(catalogsResponse);
    } catch (error) {
      setPlans([]);
      setRuleCatalogs([]);
      setErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setLoading(false);
    }
  }, [canView]);

  React.useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const resetForm = React.useCallback(() => {
    setFormState(resolveInitialState());
    setEditSelectedGroups([]);
    setEditingPlanExistingGroups([]);
    setIsLoadingEditGroups(false);
    setIsEditingEffectiveFromLocked(false);
    setIsEditingEffectiveToLocked(false);
    setEditReason('');
    setEditingPlanCurrentStatus('');
    setEditingPlanId('');
  }, []);

  const resetCreateForm = React.useCallback(() => {
    setCreateFormState(resolveInitialState());
    setCreateFromArchivedSource(null);
    setSelectedGroups([]);
  }, []);

  const catalogGroups = React.useMemo(() => {
    return ruleCatalogs.reduce((groups, catalog) => {
      const groupKey = catalog.coafGroup || 'Outros';
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(catalog);
      return groups;
    }, {});
  }, [ruleCatalogs]);

  const groupKeys = React.useMemo(() => {
    return Object.keys(catalogGroups).sort((a, b) => {
      const left = COAF_GROUP_ORDER.indexOf(a);
      const right = COAF_GROUP_ORDER.indexOf(b);
      return (left === -1 ? 999 : left) - (right === -1 ? 999 : right);
    });
  }, [catalogGroups]);

  const filteredPlans = React.useMemo(() => (
    planStatusFilter === 'all' ? plans : plans.filter((plan) => plan.status === planStatusFilter)
  ), [planStatusFilter, plans]);

  const selectedRulesPreview = React.useMemo(() => {
    if (!selectedGroups.length) {
      return [];
    }

    return ruleCatalogs
      .filter((catalog) => selectedGroups.includes(catalog.coafGroup))
      .map((catalog) => ({
        id: catalog.id,
        code: catalog.ruleCode,
        title: catalog.title,
        coafGroup: catalog.coafGroup,
        status: catalog.status,
      }));
  }, [ruleCatalogs, selectedGroups]);

  const handleEditPlan = React.useCallback(async (plan) => {
    const planFormState = buildPlanFormFromPlan(plan);
    const effectiveFrom = toDateInputValue(plan?.raw?.effective_from);
    const effectiveTo = toDateInputValue(plan?.raw?.effective_to);
    const effectiveFromDate = parseDateStart(effectiveFrom);

    setEditingPlanId(plan.id);
    setEditingPlanCurrentStatus(String(plan.status || '').trim().toLowerCase());
    setIsEditingEffectiveFromLocked(Boolean(effectiveFromDate && new Date() >= effectiveFromDate));
    setIsEditingEffectiveToLocked(Boolean(effectiveTo));
    setActiveTab(TAB_VALUES.edit);
    setErrorMessage('');
    setSuccessMessage('');
    setEditSelectedGroups([]);
    setEditingPlanExistingGroups([]);
    setEditReason('');
    setFormState(planFormState);

    setIsLoadingEditGroups(true);

    try {
      const planRules = await getAdminPlanRules(plan.id);
      const existingGroups = Array.from(new Set(
        planRules
          .map((rule) => asDisplayText(rule?.coafGroup))
          .filter((group) => group && group !== '-')
      ));

      setEditingPlanExistingGroups(existingGroups);
    } catch (error) {
      setEditingPlanExistingGroups([]);
      setErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setIsLoadingEditGroups(false);
    }
  }, []);

  const handleToggleGroup = React.useCallback((group) => {
    setSelectedGroups((current) => (
      current.includes(group)
        ? current.filter((item) => item !== group)
        : [...current, group]
    ));
  }, []);

  const handleToggleEditGroup = React.useCallback((group) => {
    setEditSelectedGroups((current) => (
      current.includes(group)
        ? current.filter((item) => item !== group)
        : [...current, group]
    ));
  }, []);

  const handleCreatePlan = React.useCallback(async () => {
    if (!canManage) {
      return;
    }

    if (!selectedGroups.length) {
      setErrorMessage('Selecione ao menos um grupo para compor o plano.');
      return;
    }

    if (!String(createFormState.effectiveFrom || '').trim()) {
      setErrorMessage('Vigência inicial do novo plano é obrigatória.');
      return;
    }

    if (createFromArchivedSource) {
      const changedKeyFields = ['name', 'code', 'targetSegment'].filter((field) => (
        String(createFormState[field] || '').trim() !== String(createFromArchivedSource[field] || '').trim()
      ));

      if (!changedKeyFields.length) {
        setErrorMessage('Altere ao menos um campo-chave (nome, código ou segmento) antes de criar a nova versão.');
        return;
      }
    }

    const selectedRuleCatalogs = ruleCatalogs.filter((catalog) => selectedGroups.includes(catalog.coafGroup));

    if (!selectedRuleCatalogs.length) {
      setErrorMessage('Nenhum catálogo de regras encontrado para os grupos selecionados.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await createAdminPlan({
        ...createFormState,
        planRules: selectedRuleCatalogs.map((catalog) => ({
          ruleCatalogId: catalog.id,
          enabledByDefault: true,
          canClientDisable: false,
          dependencyPolicy: 'strict',
        })),
      });

      await loadPlans();
      resetCreateForm();
      setSuccessMessage('Plano criado com sucesso.');
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [canManage, createFormState, createFromArchivedSource, loadPlans, resetCreateForm, ruleCatalogs, selectedGroups]);

  const handleSubmit = React.useCallback(async (overrides = {}) => {
    const targetStatus = overrides.status || formState.status;
    const reason = String(overrides.reason ?? editReason ?? '').trim();

    if (!canManage) {
      return false;
    }

    if (!editingPlanId) {
      setErrorMessage('Selecione um plano para editar.');
      return false;
    }

    if (editingPlanCurrentStatus === 'retired') {
      setErrorMessage('Plano arquivado é imutável. Crie uma nova versão com campos-chave diferentes.');
      return false;
    }

    if (!reason) {
      setErrorMessage('Informe a justificativa da atualização do plano.');
      return false;
    }

    if (!String(formState.effectiveFrom || '').trim()) {
      setErrorMessage('Vigência inicial do plano é obrigatória.');
      return false;
    }

    const pendingGroupAdditions = editingPlanCurrentStatus === 'active' || targetStatus === 'retired'
      ? []
      : editSelectedGroups.filter((group) => !editingPlanExistingGroups.includes(group));

    const selectedRuleCatalogs = ruleCatalogs.filter((catalog) => pendingGroupAdditions.includes(catalog.coafGroup));

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const updatePayload = {
        ...formState,
        status: targetStatus,
        reason,
        planRules: selectedRuleCatalogs.map((catalog) => ({
          ruleCatalogId: catalog.id,
          enabledByDefault: true,
          canClientDisable: false,
          dependencyPolicy: 'strict',
        })),
      };

      if (isEditingEffectiveFromLocked) {
        delete updatePayload.effectiveFrom;
      }

      await updateAdminPlan(editingPlanId, updatePayload);
      await loadPlans();
      resetForm();
      setSuccessMessage(overrides.successMessage || 'Plano atualizado com sucesso.');
      return true;
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [canManage, editReason, editSelectedGroups, editingPlanCurrentStatus, editingPlanExistingGroups, editingPlanId, formState, isEditingEffectiveFromLocked, loadPlans, resetForm, ruleCatalogs]);

  const handleOpenArchivePlanDialog = React.useCallback(() => {
    setArchivePlanDialog({ open: true, reason: '' });
    setErrorMessage('');
    setSuccessMessage('');
  }, []);

  const handleCloseArchivePlanDialog = React.useCallback(() => {
    if (submitting) {
      return;
    }

    setArchivePlanDialog({ open: false, reason: '' });
  }, [submitting]);

  const handleArchivePlanReasonChange = React.useCallback((value) => {
    setArchivePlanDialog((current) => ({ ...current, reason: value }));
  }, []);

  const handleConfirmArchivePlan = React.useCallback(async () => {
    const trimmedReason = archivePlanDialog.reason.trim();

    if (!trimmedReason) {
      setErrorMessage('Informe o motivo do arquivamento.');
      return;
    }

    const succeeded = await handleSubmit({
      status: 'retired',
      reason: trimmedReason,
      successMessage: 'Plano arquivado com sucesso.',
    });

    if (succeeded) {
      setArchivePlanDialog({ open: false, reason: '' });
    }
  }, [archivePlanDialog, handleSubmit]);

  const handleCopyArchivedToCreate = React.useCallback(() => {
    if (!canManage) {
      return;
    }

    if (!editingPlanId) {
      setErrorMessage('Selecione um plano arquivado para copiar os dados.');
      return;
    }

    if (editingPlanCurrentStatus !== 'retired') {
      setErrorMessage('A cópia para criação só está disponível para plano arquivado.');
      return;
    }

    if (isLoadingEditGroups) {
      setErrorMessage('Aguarde o carregamento dos grupos do plano antes de copiar.');
      return;
    }

    const currentVersion = Number.parseInt(String(formState.version || '').trim(), 10);
    const suggestedVersion = Number.isNaN(currentVersion) ? String(formState.version || '') : String(currentVersion + 1);

    setCreateFormState({
      name: formState.name,
      code: formState.code,
      description: formState.description,
      targetSegment: formState.targetSegment,
      version: suggestedVersion,
      status: 'draft',
      effectiveFrom: formState.effectiveFrom,
      effectiveTo: '',
    });
    setCreateFromArchivedSource({
      name: formState.name,
      code: formState.code,
      targetSegment: formState.targetSegment,
    });
    setSelectedGroups([...editingPlanExistingGroups]);
    setActiveTab(TAB_VALUES.create);
    setErrorMessage('');
    setSuccessMessage('Dados do plano arquivado copiados para criação de nova versão. Ajuste os campos e salve o novo plano.');
  }, [canManage, editingPlanCurrentStatus, editingPlanExistingGroups, editingPlanId, formState, isLoadingEditGroups]);

  const handleTabChange = React.useCallback((event, nextTab) => {
    setActiveTab(nextTab);
  }, []);

  if (loading) {
    return (
      <Card variant="outlined" sx={{ width: '100%' }}>
        <CardContent>
          <Stack sx={{ alignItems: 'center', py: 6 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
              Carregando planos...
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography component="h1" variant="h5">Gestão de planos</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Cadastre novos planos e revise versões existentes com controle sobre vigência, status e composição de regras.
              </Typography>

              {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
              <SuccessToast message={successMessage} onClose={() => setSuccessMessage('')} />
              {!canManage ? <Alert severity="info">Seu perfil possui acesso somente leitura para esta tela.</Alert> : null}

              <Tabs value={activeTab} onChange={handleTabChange} aria-label="Fluxo de planos">
                <Tab value={TAB_VALUES.create} label="Criar plano" />
                <Tab value={TAB_VALUES.edit} label="Editar planos" />
              </Tabs>
            </Stack>
          </CardContent>
        </Card>

        {activeTab === TAB_VALUES.create ? (
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Typography component="h2" variant="h6">Criar plano</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Informe os metadados do plano e selecione os grupos COAF que vão compor o mesmo.
                </Typography>
                {createFromArchivedSource ? (
                  <Alert severity="info">Esta criação foi iniciada a partir de um plano arquivado. Altere ao menos um campo-chave (nome, código ou segmento) antes de salvar.</Alert>
                ) : null}

                <FormSection title="Identificação do plano" description="Defina os dados principais usados para reconhecer o plano no catálogo administrativo.">
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField fullWidth label="Nome" value={createFormState.name} onChange={(event) => setCreateFormState((current) => ({ ...current, name: event.target.value }))} disabled={!canManage || submitting} />
                    <TextField fullWidth label="Código" value={createFormState.code} onChange={(event) => setCreateFormState((current) => ({ ...current, code: event.target.value }))} disabled={!canManage || submitting} />
                  </Stack>
                  <TextField fullWidth label="Descrição" value={createFormState.description} onChange={(event) => setCreateFormState((current) => ({ ...current, description: event.target.value }))} disabled={!canManage || submitting} />
                  <TextField fullWidth label="Segmento alvo (opcional)" value={createFormState.targetSegment} onChange={(event) => setCreateFormState((current) => ({ ...current, targetSegment: event.target.value }))} disabled={!canManage || submitting} />
                </FormSection>

                <FormSection title="Ciclo de vida" description="Todo plano novo começa como rascunho — publique quando estiver pronto, na aba Editar planos. Controle versão e janela de vigência abaixo.">
                  <Box sx={{ maxWidth: { xs: '100%', md: 220 } }}>
                    <TextField fullWidth label="Versão" value={createFormState.version} onChange={(event) => setCreateFormState((current) => ({ ...current, version: event.target.value }))} disabled={!canManage || submitting} />
                  </Box>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField fullWidth type="date" label="Vigência inicial" value={createFormState.effectiveFrom} onChange={(event) => setCreateFormState((current) => ({ ...current, effectiveFrom: event.target.value }))} InputLabelProps={{ shrink: true }} inputProps={{ 'data-testid': 'new-plan-effective-from' }} disabled={!canManage || submitting} required />
                    <TextField fullWidth type="date" label="Vigência final" value={createFormState.effectiveTo} onChange={(event) => setCreateFormState((current) => ({ ...current, effectiveTo: event.target.value }))} InputLabelProps={{ shrink: true }} disabled={!canManage || submitting} />
                  </Stack>
                </FormSection>

                <FormSection title="Composição regulatória" description="Selecione os grupos COAF que formarão a base de regras do plano.">
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Grupos COAF</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Selecione pelo menos um grupo. Todos os catálogos do grupo entram no novo plano.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
                      {groupKeys.map((group) => (
                        <Stack key={group} direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
                          <FormControlLabel
                            control={(
                              <Checkbox
                                checked={selectedGroups.includes(group)}
                                onChange={() => handleToggleGroup(group)}
                                disabled={!canManage || submitting}
                              />
                            )}
                            label={`Grupo ${group}`}
                          />
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {`${catalogGroups[group].length} regras`}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>

                  <RulesSelectionPreview
                    title="Mapa de regras do novo plano"
                    subtitle="Atualiza em tempo real com base nos grupos COAF selecionados."
                    rules={selectedRulesPreview}
                    highlightedGroups={selectedGroups}
                    emptyMessage="Selecione ao menos um grupo para visualizar as regras do plano."
                  />
                </FormSection>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'stretch', sm: 'center' }, pt: 0.5 }}>
                  <Button variant="contained" onClick={handleCreatePlan} disabled={!canManage || submitting || !selectedGroups.length}>Criar plano</Button>
                  <Button variant="outlined" onClick={resetCreateForm} disabled={submitting}>Limpar</Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        {activeTab === TAB_VALUES.edit ? (
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Typography component="h2" variant="h6">Editar planos</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Escolha um plano na tabela para carregar o formulário de edição, informar a justificativa e, se necessário, adicionar novos grupos.
                </Typography>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { xs: 'stretch', md: 'center' }, justifyContent: 'space-between' }}>
                  <Stack spacing={0.5} sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{`Planos disponíveis para edição (${filteredPlans.length})`}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Use a listagem abaixo para localizar o plano desejado e iniciar a edição do registro correto.
                    </Typography>
                  </Stack>
                  <FormControl sx={{ minWidth: 220 }} size="small">
                    <InputLabel id="plan-status-filter-label">Filtrar por status</InputLabel>
                    <Select
                      labelId="plan-status-filter-label"
                      value={planStatusFilter}
                      label="Filtrar por status"
                      onChange={(event) => setPlanStatusFilter(event.target.value)}
                    >
                      <MenuItem value="all">Todos</MenuItem>
                      {PLAN_STATUS_OPTIONS.map((status) => (
                        <MenuItem key={status} value={status}>{PLAN_STATUS_LABELS[status]}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Código</TableCell>
                        <TableCell>Nome</TableCell>
                        <TableCell>Versão</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Segmento</TableCell>
                        <TableCell>Grupos COAF</TableCell>
                        <TableCell>Vigência</TableCell>
                        <TableCell>Última alteração</TableCell>
                        <TableCell>Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPlans.map((plan) => (
                        <TableRow key={plan.id} hover>
                          <TableCell>{asDisplayText(plan.code)}</TableCell>
                          <TableCell>{asDisplayText(plan.name)}</TableCell>
                          <TableCell>{asDisplayText(plan.version)}</TableCell>
                          <TableCell>{resolvePlanStatusLabel(plan.status)}</TableCell>
                          <TableCell>{asDisplayText(plan.targetSegment)}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                              {(plan.coafGroups || []).length ? plan.coafGroups.map((group) => (
                                <Chip key={group} size="small" label={group} variant="outlined" />
                              )) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>-</Typography>}
                            </Stack>
                          </TableCell>
                          <TableCell>{`${formatTemporalHumanized(plan.effectiveFrom)} - ${formatTemporalHumanized(plan.effectiveTo)}`}</TableCell>
                          <TableCell>{resolveLatestChangeSummary(plan.latestChange || plan.raw?.latest_change)}</TableCell>
                          <TableCell>
                            <Button variant="text" size="small" onClick={() => handleEditPlan(plan)} disabled={!canManage || submitting}>Editar plano</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!filteredPlans.length ? (
                        <TableRow>
                          <TableCell colSpan={9}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Nenhum plano encontrado.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </TableContainer>

                {!editingPlanId ? (
                  <Alert severity="info">Selecione um plano na tabela para liberar o formulário de edição.</Alert>
                ) : null}
                {editingPlanId && editingPlanCurrentStatus === 'active' ? (
                  <Alert severity="warning">Este plano está ativo. Novos grupos só podem ser incluídos em uma nova versão. Atualizações de campos permitidos, como vigência final ainda não definida, continuam liberadas.</Alert>
                ) : null}
                {editingPlanId && editingPlanCurrentStatus === 'retired' ? (
                  <Alert severity="warning">Plano arquivado é imutável. Para criar nova versão, copie os dados para a tela de criação.</Alert>
                ) : null}
                {editingPlanId && isEditingEffectiveFromLocked ? (
                  <Alert severity="info">A vigência inicial não pode ser alterada após o início do plano.</Alert>
                ) : null}
                {editingPlanId && isEditingEffectiveToLocked ? (
                  <Alert severity="info">A vigência final não pode ser alterada após ser configurada.</Alert>
                ) : null}
                {editingPlanId && isLoadingEditGroups ? (
                  <Alert severity="info">Carregando grupos já vinculados ao plano...</Alert>
                ) : null}

                <Divider />

                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">Detalhes do plano selecionado</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Revise os campos abaixo antes de salvar. As restrições de status e vigência continuam aplicadas durante a edição.
                  </Typography>
                </Stack>

                <FormSection title="Identificação do plano" description="Revise os dados principais do plano selecionado antes de salvar a alteração.">
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField fullWidth label="Nome" value={formState.name} onChange={(event) => setFormState((current) => ({ ...current, name: event.target.value }))} disabled={!canManage || !editingPlanId || submitting || editingPlanCurrentStatus === 'retired'} />
                    <TextField fullWidth label="Código" value={formState.code} onChange={(event) => setFormState((current) => ({ ...current, code: event.target.value }))} disabled={!canManage || !editingPlanId || submitting || editingPlanCurrentStatus === 'retired'} />
                  </Stack>
                  <TextField fullWidth label="Descrição" value={formState.description} onChange={(event) => setFormState((current) => ({ ...current, description: event.target.value }))} disabled={!canManage || !editingPlanId || submitting || editingPlanCurrentStatus === 'retired'} />
                  <TextField fullWidth label="Segmento alvo (opcional)" value={formState.targetSegment} onChange={(event) => setFormState((current) => ({ ...current, targetSegment: event.target.value }))} disabled={!canManage || !editingPlanId || submitting || editingPlanCurrentStatus === 'retired'} />
                </FormSection>

                <FormSection title="Ciclo de vida" description="Ajuste versão, status e vigência dentro das restrições do plano selecionado.">
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 320px) minmax(260px, 420px)' }, gap: 2, alignItems: 'start' }}>
                    <TextField fullWidth label="Versão" value={formState.version} onChange={(event) => setFormState((current) => ({ ...current, version: event.target.value }))} disabled={!canManage || !editingPlanId || submitting || editingPlanCurrentStatus === 'retired'} />
                    <FormControl fullWidth disabled={!canManage || !editingPlanId || submitting || editingPlanCurrentStatus === 'retired'}>
                      <InputLabel id="plan-status-label">Status</InputLabel>
                      <Select
                        labelId="plan-status-label"
                        value={formState.status}
                        label="Status"
                        onChange={(event) => {
                          const nextStatus = event.target.value;

                          if (nextStatus === 'retired') {
                            handleOpenArchivePlanDialog();
                            return;
                          }

                          setFormState((current) => ({ ...current, status: nextStatus }));
                        }}
                      >
                        {PLAN_STATUS_OPTIONS.map((status) => (
                          <MenuItem key={status} value={status} disabled={editingPlanCurrentStatus === 'retired' && status !== 'retired'}>{PLAN_STATUS_LABELS[status]}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField fullWidth type="date" label="Vigência inicial" value={formState.effectiveFrom} onChange={(event) => setFormState((current) => ({ ...current, effectiveFrom: event.target.value }))} InputLabelProps={{ shrink: true }} disabled={!canManage || !editingPlanId || submitting || isEditingEffectiveFromLocked || editingPlanCurrentStatus === 'retired'} required />
                    <TextField fullWidth type="date" label="Vigência final" value={formState.effectiveTo} onChange={(event) => setFormState((current) => ({ ...current, effectiveTo: event.target.value }))} InputLabelProps={{ shrink: true }} disabled={!canManage || !editingPlanId || submitting || isEditingEffectiveToLocked || editingPlanCurrentStatus === 'retired'} />
                  </Stack>
                </FormSection>

                <FormSection title="Composição regulatória" description="Adicione somente novos grupos COAF ao plano atual quando a operação estiver liberada.">
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Adicionar grupos COAF ao plano</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Grupos já vinculados aparecem marcados e bloqueados. Selecione apenas novos grupos para inclusão.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
                      {groupKeys.map((group) => (
                        <Stack key={`edit-${group}`} direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
                          <FormControlLabel
                            control={(
                              <Checkbox
                                checked={editingPlanExistingGroups.includes(group) || editSelectedGroups.includes(group)}
                                onChange={() => handleToggleEditGroup(group)}
                                disabled={!canManage || !editingPlanId || submitting || editingPlanCurrentStatus === 'active' || editingPlanCurrentStatus === 'retired' || isLoadingEditGroups || editingPlanExistingGroups.includes(group)}
                              />
                            )}
                            label={`Grupo ${group}`}
                          />
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {`${catalogGroups[group].length} regras`}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </FormSection>

                <FormSection title="Governança da alteração" description="Registre a justificativa para manter a trilha administrativa do plano.">
                  <TextField
                    fullWidth
                    label="Justificativa da atualização"
                    value={editReason}
                    onChange={(event) => setEditReason(event.target.value)}
                    disabled={!canManage || !editingPlanId || submitting || editingPlanCurrentStatus === 'retired'}
                    required
                  />
                </FormSection>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'stretch', sm: 'center' }, pt: 0.5 }}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!canManage || !editingPlanId || submitting || editingPlanCurrentStatus === 'retired'}
                    title={editingPlanCurrentStatus === 'active'
                      ? 'Plano ativo: a inclusão de novos grupos está bloqueada. Para incluir grupos, crie uma nova versão do plano.'
                      : editingPlanCurrentStatus === 'retired'
                        ? 'Plano arquivado é imutável. Crie uma nova versão.'
                        : ''}
                  >
                    Salvar plano
                  </Button>
                  {editingPlanId && editingPlanCurrentStatus === 'retired' ? (
                    <Button
                      variant="outlined"
                      onClick={handleCopyArchivedToCreate}
                      disabled={!canManage || submitting || isLoadingEditGroups}
                    >
                      Copiar para criação
                    </Button>
                  ) : null}
                  <Button variant="outlined" onClick={resetForm} disabled={submitting}>Limpar</Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ) : null}
      </Stack>

      <AuditStatusConfirmDialog
        open={archivePlanDialog.open}
        title="Arquivar plano"
        description={`Isso vai arquivar o plano "${formState.name}". Um plano arquivado é imutável — para novas regras, crie uma nova versão. Informe o motivo para o registro de auditoria.`}
        reason={archivePlanDialog.reason}
        submitting={submitting}
        confirmLabel="Arquivar"
        confirmColor="warning"
        onReasonChange={handleArchivePlanReasonChange}
        onClose={handleCloseArchivePlanDialog}
        onConfirm={handleConfirmArchivePlan}
      />
    </Box>
  );
}
